/**
 * ONE OUTCOME, ONE MESSAGE — and `not-built-yet` never reads like "you're offline".
 * ==============================================================================
 *
 * `src/core/api.ts` is organised around a single distinction and this component is the place it
 * either survives to the screen or dies: three routes deliberately answer **503 with a NAMED
 * BLOCKER** (`PRD-0.12-salesforce-identity`, `C-42-cloudfront-signing-key`,
 * `C-12-technician-attribution`) rather than an empty result, because — the server file's own words —
 * *"a technician with no jobs listed cannot tell 'you have no work today' from 'the integration is
 * down'."*
 *
 * If those arrived on screen as "check your connection", the client would recreate on the phone exactly
 * the ambiguity the server went to trouble to remove, and it would send a technician to stand in a car
 * park hunting for a signal over a problem in our backlog. So:
 *
 *  · every variant of {@link ApiOutcome} gets its own title, its own body, and its own
 *    `data-outcome` attribute;
 *  · `not-built-yet` **names the blocker** and says in as many words that this is not a connection
 *    problem and that retrying will not help;
 *  · `offline` says the opposite — that it IS connectivity, that it is normal, and that work is still
 *    being saved;
 *  · `network-error` is kept apart from both, because `fetch` rejecting could be DNS, TLS, a captive
 *    portal or a dropped connection and we genuinely do not know which.
 *
 * The `switch` is exhaustive with a `never` guard. A new outcome variant therefore fails to compile
 * here rather than falling through to a generic "something went wrong", which is how these
 * distinctions get quietly lost.
 *
 * ─── AND NOW IN EVERY LANGUAGE, WITH THE SAME DISTINCTION ──────────────────────────────────────
 *
 * The wording moved to `src/i18n/uiStrings.ts` under `ui.api.*` — one title key and one body key per
 * outcome, never a shared key, so **the distinction is carried by the catalogue and not only by this
 * switch**. Two outcomes that reuse one key would be the same defect as two outcomes that reuse one
 * sentence, and a reviewer reading a French catalogue cannot see a shared `case` in a TypeScript file.
 * `test/i18n/blocking-messages.test.ts` asserts `not-built-yet` and `network-error` share no sentence
 * in English *and* under a full non-English catalogue.
 *
 * Two things stay English on purpose, both recorded in the translator notes:
 *
 *  · **`{what}`** — the caller passes an English verb phrase (`compose/screens.tsx`: "look up that
 *    kiosk"). Those literals are in a file this work does not own, so a translated notice reads as the
 *    target language wrapped around an English clause until they are externalised too. Honest partial
 *    state, recorded rather than hidden.
 *  · **the `detail` line** — a blocker name, an HTTP status, a request id. It is what a technician reads
 *    aloud to support, so it must be the same characters in every locale, for the same reason
 *    `format.ts` refuses to localise `HH:MM`. Only its *label* is translated.
 */

import type { ApiOutcome } from '@/core/api';
import { englishMessages } from '@/i18n/messages';
import type { Messages } from '@/i18n/messages';
import { NOTICE_KEYS } from '@/i18n/uiStrings';

export interface ApiNoticeProps {
  readonly outcome: ApiOutcome<unknown>;
  /** What was being attempted, in the technician's words, e.g. `look up kiosk 78995141`. */
  readonly what: string;
  /** The active locale's messages. Omit for English. */
  readonly messages?: Messages;
}

type Tone = 'offline' | 'info' | 'danger';

interface Notice {
  readonly tone: Tone;
  readonly title: string;
  readonly body: string;
  /** The `ui.api.*` keys behind `title` and `body`. Distinct per outcome — see the header. */
  readonly titleKey: string;
  readonly bodyKey: string;
  /**
   * Shown in a monospace line for support to quote. Never a stack, never a token, and **never
   * translated** — it is an identifier, not prose.
   */
  readonly detail?: string;
}

export function describeOutcome(
  outcome: ApiOutcome<unknown>,
  what: string,
  messages: Messages = englishMessages(),
): Notice {
  /** One title/body pair from one outcome's two keys. Keeps key and text impossible to mismatch. */
  const say = (
    tone: Tone,
    titleKey: string,
    bodyKey: string,
    params?: Readonly<Record<string, string | number>>,
  ): Omit<Notice, 'detail'> => ({
    tone,
    title: messages.m(titleKey),
    body: messages.m(bodyKey, params),
    titleKey,
    bodyKey,
  });

  switch (outcome.kind) {
    case 'ok':
    case 'accepted':
      return say('info', NOTICE_KEYS.okTitle, NOTICE_KEYS.okBody, { what });

    case 'offline':
      return say('offline', NOTICE_KEYS.offlineTitle, NOTICE_KEYS.offlineBody, { what });

    case 'network-error':
      return {
        ...say('danger', NOTICE_KEYS.networkErrorTitle, NOTICE_KEYS.networkErrorBody),
        detail: outcome.detail,
      };

    /**
     * The variant this whole file exists for. Note what it does NOT say: it does not mention the
     * connection, and it does not suggest trying again.
     */
    case 'not-built-yet':
      return {
        ...say('info', NOTICE_KEYS.notBuiltYetTitle, NOTICE_KEYS.notBuiltYetBody, {
          what,
          blocker: outcome.blocker,
        }),
        detail: `blocker ${outcome.blocker}${
          outcome.requestId !== null ? ` · request ${outcome.requestId}` : ''
        }`,
      };

    case 'server-error':
      return {
        ...say('danger', NOTICE_KEYS.serverErrorTitle, NOTICE_KEYS.serverErrorBody, { what }),
        detail: `HTTP ${outcome.status}${outcome.requestId !== null ? ` · request ${outcome.requestId}` : ''}`,
      };

    case 'not-found':
      return {
        ...say('danger', NOTICE_KEYS.notFoundTitle, NOTICE_KEYS.notFoundBody),
        // Spread rather than `: undefined` — `exactOptionalPropertyTypes` treats an explicit
        // `undefined` as a different type from an absent key.
        ...(outcome.requestId !== null ? { detail: `request ${outcome.requestId}` } : {}),
      };

    case 'rejected':
      return {
        ...say('danger', NOTICE_KEYS.rejectedTitle, NOTICE_KEYS.rejectedBody, { what }),
        detail: `${outcome.code} · HTTP ${outcome.status}`,
      };

    case 'conflict':
      return {
        ...say('danger', NOTICE_KEYS.conflictTitle, NOTICE_KEYS.conflictBody),
        detail: outcome.code,
      };

    case 'unauthenticated':
      return {
        ...say('danger', NOTICE_KEYS.unauthenticatedTitle, NOTICE_KEYS.unauthenticatedBody, { what }),
        detail: `HTTP ${outcome.status}`,
      };

    /**
     * Distinct from `unauthenticated` on purpose. There is no session to expire: sign-in has not been
     * built, so **nothing was sent at all**. A 401-shaped message here would send someone looking for
     * a password problem.
     */
    case 'auth-not-configured':
      return say('info', NOTICE_KEYS.authNotConfiguredTitle, NOTICE_KEYS.authNotConfiguredBody, { what });

    default: {
      // Exhaustiveness guard. A new `ApiOutcome` variant must get its own wording here rather than
      // silently inheriting somebody else's.
      const unreachable: never = outcome;
      return unreachable;
    }
  }
}

/**
 * `alert` is assertive: it interrupts whatever a screen reader is saying. That is right for the four
 * outcomes where something is actually wrong with the request, and wrong for the ones where nothing is.
 *
 * Every notice used to be `role="alert"`, including `offline` — whose own body text says *"this is not a
 * fault"* — and `ok`. Interrupting a technician to assert that they are offline and everything is fine
 * is the audible version of the reassuring-red-banner problem the rest of this file avoids: it teaches
 * them that the app shouts at random, and then the shout that matters is the one they talk over.
 *
 * `offline` and `info` (which is `not-built-yet`, `auth-not-configured` and `ok`) are therefore polite
 * status. `danger` stays assertive.
 */
function ariaRoleFor(tone: Tone): 'alert' | 'status' {
  return tone === 'danger' ? 'alert' : 'status';
}

export function ApiNotice({ outcome, what, messages }: ApiNoticeProps): JSX.Element {
  const resolved = messages ?? englishMessages();
  const notice = describeOutcome(outcome, what, resolved);
  return (
    <div
      className={`banner banner--${notice.tone}`}
      role={ariaRoleFor(notice.tone)}
      {...(notice.tone === 'danger' ? {} : { 'aria-live': 'polite' as const })}
      data-outcome={outcome.kind}
    >
      <p className="banner__title">{notice.title}</p>
      <p className="banner__body">{notice.body}</p>
      {/*
        The detail is the string support asks for — a blocker name, an HTTP status, a request id. It was
        `.mono muted`: the smallest, dimmest text in the notice, which is the wrong end of the hierarchy
        for the one part a technician is being asked to read out over a phone in a car park. It is now
        labelled with what to do with it and set at a size that can be read aloud from a phone at arm's
        length.
      */}
      {notice.detail !== undefined && (
        <p className="fx-quote">
          <span className="fx-quote__label">{resolved.m(NOTICE_KEYS.quoteLabel)}</span>
          <span className="mono fx-quote__value">{notice.detail}</span>
        </p>
      )}
    </div>
  );
}
